import { getEventFields } from "./eventsTimelineView";

/**
 * Turn the flat CloudWatch event stream into a conversation the way a Claude
 * Code transcript reads: per run, per turn, prose then tool invocations.
 *
 * Why this exists rather than rendering events one-to-one: for a 400-event
 * window on a real run (run-2e8fc667d7cd), the stream is
 *
 *   242 message · 113 tool_call · 14 turn_start · 12 llm_call · 8 turn_end
 *
 * and of the 242 messages, 113 are `role=tool_result` (already paired onto
 * their call) and ~100 are `role=assistant` carrying NO prose — only a
 * `toolUses` list naming the tools that the very next `tool_call` events
 * describe in full. Rendering every event is therefore ~242 cards for ~133
 * facts. This collapses the stream to those facts:
 *
 *   - a tool invocation is ONE item: name + args + duration + its result
 *   - assistant messages contribute only their text
 *   - turn_start/turn_end become a turn header, llm_call its metadata
 *   - tool_result messages disappear into the call they belong to
 *
 * Reads `fields` only, never the raw `data` string: the events API truncates
 * `data` mid-JSON on large payloads, while the backend already extracts
 * `result_text` into `fields` for tool_result messages.
 */

// A run's own events, in order, plus who its parent is.
function splitByRun(events) {
  const runs = new Map();
  for (const ev of events) {
    const runId = ev.run_id || "unknown";
    let run = runs.get(runId);
    if (!run) {
      run = {
        runId,
        agentId: ev.agent_id || null,
        parentRunId: ev.parent_run_id || null,
        events: [],
      };
      runs.set(runId, run);
    }
    // parent_run_id is only stamped on some event types; first non-null wins.
    if (!run.parentRunId && ev.parent_run_id) run.parentRunId = ev.parent_run_id;
    if (!run.agentId && ev.agent_id) run.agentId = ev.agent_id;
    run.events.push(ev);
  }
  return runs;
}

function emptyTurn(index, seq) {
  return {
    index,
    // Monotonic within the run. `index` comes from turn_start and is NOT
    // unique — a run can emit the same turn_index twice (or content can land
    // in the implicit first turn before turn_start 1 arrives), which collided
    // as a React key. Use seq for keys, index for display.
    seq,
    llm: null,
    // Position of the llm_call inside the ORIGINAL event array — ContextMeter
    // needs it to estimate the breakdown from preceding events when the
    // payload carries no measured `context`.
    llmEventIndex: null,
    llmCalls: 0,
    costUsd: 0,
    durationMs: 0,
    texts: [],
    items: [],
  };
}

function toMillis(timestamp) {
  if (timestamp == null) return null;
  if (typeof timestamp === "number") return timestamp;
  const parsed = Date.parse(timestamp);
  return Number.isNaN(parsed) ? null : parsed;
}

function hasContent(turn) {
  return turn.texts.length > 0 || turn.items.length > 0 || turn.llmCalls > 0;
}

/**
 * Conversation for one run:
 * {turns, sessionId, websiteId, prompt, contextManifest, firstTs}.
 *
 * `originalIndex` maps an event back to its position in the unfiltered array
 * the caller holds; ContextMeter estimates a breakdown from everything that
 * precedes an llm_call, so it needs the index in THAT array, not in this run's
 * slice. Optional — without it the local index is used, which is correct when
 * the caller passes the full array anyway.
 */
export function buildRunConversation(runEvents, originalIndex = null) {
  // Results arrive as their own event; index them so each call can absorb its.
  const resultsByCallId = new Map();
  for (const ev of runEvents) {
    if (ev.type !== "message") continue;
    const f = getEventFields(ev);
    if (f.role === "tool_result" && f.tool_call_id) {
      resultsByCallId.set(f.tool_call_id, f);
    }
  }

  const turns = [];
  let seq = 0;
  let current = emptyTurn(1, seq);
  let sessionId = null;
  let websiteId = null;
  let prompt = null;
  let contextManifest = null;
  let firstTs = null;

  const flush = () => {
    if (hasContent(current)) turns.push(current);
  };

  for (let localIdx = 0; localIdx < runEvents.length; localIdx += 1) {
    const ev = runEvents[localIdx];
    const type = ev.type || "event";
    const f = getEventFields(ev);
    const ts = toMillis(ev.timestamp);
    if (ts != null && (firstTs == null || ts < firstTs)) firstTs = ts;

    if (type === "agent_start") {
      sessionId = f.session_id || sessionId;
      websiteId = f.website_id || websiteId;
      continue;
    }

    // Prompt provenance for the run. Deliberately not a turn item: it belongs
    // beside the run's prompt disclosure, not inline in the conversation.
    if (type === "agent_context") {
      contextManifest = f.manifest || contextManifest;
      continue;
    }

    if (type === "turn_start") {
      flush();
      seq += 1;
      current = emptyTurn(f.turn_index ?? turns.length + 1, seq);
      continue;
    }

    if (type === "turn_end") continue;

    if (type === "llm_call") {
      // Several calls can land in one turn (retries, continuation). Keep the
      // model/usage of the last and accumulate the spend.
      current.llm = f;
      current.llmEventIndex = originalIndex ? originalIndex.get(ev) ?? localIdx : localIdx;
      current.llmCalls += 1;
      current.costUsd += Number(f.cost_usd) || 0;
      current.durationMs += Number(f.duration_ms) || 0;
      continue;
    }

    if (type === "hook_notice") {
      // CloudWatch stamps is_error=true on warn-severity hook notices too, so
      // trusting it would report a context_guard trim as a run error — every
      // worker of a 24/24-ok fan-out showed "1 err". Severity is the truth.
      const severity = f.severity || (ev.is_error ? "error" : "info");
      current.items.push({
        kind: "hook",
        hook: f.hook || "hook",
        severity,
        text: f.text || "",
        isError: severity === "error",
        isWarning: severity === "warn",
      });
      continue;
    }

    if (type === "tool_call") {
      const result = f.tool_call_id ? resultsByCallId.get(f.tool_call_id) : null;
      current.items.push({
        kind: "tool",
        name: f.tool_name || "tool",
        args: f.args,
        // The step's declared "why" — the agent's own words on free-form tools
        // (browsing:evaluate and friends), or the runtime's on the calls it
        // injects itself. Null on every unannotated call and on every trace
        // written before the field shipped, which is why ToolRow keeps the arg
        // summary as its fallback rather than depending on this.
        intent: f.intent || null,
        durationMs: f.duration_ms,
        blockedReason: f.blocked_reason || null,
        toolCallId: f.tool_call_id || null,
        isError: Boolean(ev.is_error) || Boolean(result?.is_error),
        result: result
          ? {
              text: result.result_text || null,
              isError: Boolean(result.is_error),
              screenshotUrls: result.screenshot_urls || [],
            }
          : null,
        screenshotUrls: f.screenshot_urls || [],
        timestamp: ev.timestamp,
        ts: toMillis(ev.timestamp),
        // When the call BEGAN. A tool_call event is logged on completion and
        // carries the elapsed time, so `ts` is the end: fan-out:launch on
        // run-2e8fc667d7cd is stamped 5m46s after it dispatched its workers.
        // Matching children against `ts` would file them under whatever call
        // ran before the fan-out.
        startTs: (() => {
          const end = toMillis(ev.timestamp);
          if (end == null) return null;
          const dur = Number(f.duration_ms);
          return Number.isFinite(dur) ? end - dur : end;
        })(),
        // Child runs this call spawned, filled in by attachChildrenToCalls.
        children: [],
      });
      continue;
    }

    if (type === "message") {
      // tool_result events are folded into their call above.
      if (f.role === "tool_result") continue;

      const texts = (f.blocks?.texts || [])
        .map((t) => (typeof t === "string" ? t : String(t ?? "")))
        .filter((t) => t.trim());

      if (f.role === "user") {
        // The first user message is the run's prompt; later ones are replies.
        if (!prompt && texts.length) {
          prompt = texts.join("\n\n");
        } else if (texts.length) {
          current.items.push({ kind: "userReply", text: texts.join("\n\n") });
        }
        continue;
      }

      // Assistant with no prose carries only a duplicate tool-name list.
      if (texts.length) current.texts.push(...texts);
      continue;
    }

    // Anything unrecognised stays visible rather than being silently dropped.
    current.items.push({
      kind: "other",
      type,
      fields: f,
      isError: Boolean(ev.is_error),
    });
  }

  flush();
  return { turns, sessionId, websiteId, prompt, contextManifest, firstTs };
}

/**
 * Hang each child run off the parent tool call that spawned it.
 *
 * There is no explicit spawn→child link for fan-out workers the way there is
 * for `subagent_spawn` (which carries `child_run_id`): a `fan-out:launch`
 * dispatches N workers whose only tie to the parent is `parent_run_id`. So the
 * call is matched chronologically — a child belongs to the last tool call that
 * had STARTED when the child's first event fired. That puts the 24 workers
 * under the `fan-out:launch` that dispatched them instead of in a heap at the
 * bottom.
 *
 * Start, not end: a fan-out that dispatches at T and returns at T+5m46s is
 * logged at T+5m46s, so comparing against the logged timestamp files its own
 * workers under the previous call.
 *
 * Children with no timestamp, or that predate every call, stay in `trailing`
 * so nothing is dropped.
 */
export function attachChildrenToCalls(node) {
  const calls = [];
  for (const turn of node.turns || []) {
    for (const item of turn.items) {
      if (item.kind === "tool" && item.startTs != null) calls.push(item);
    }
  }
  calls.sort((a, b) => a.startTs - b.startTs);

  const trailing = [];
  for (const child of node.children || []) {
    const childTs = child.firstTs;
    if (childTs == null || !calls.length) {
      trailing.push(child);
      continue;
    }
    let owner = null;
    for (const call of calls) {
      if (call.startTs <= childTs) owner = call;
      else break;
    }
    if (owner) owner.children.push(child);
    else trailing.push(child);
  }

  for (const call of calls) {
    call.children.sort((a, b) => (a.firstTs ?? 0) - (b.firstTs ?? 0));
  }
  node.trailingChildren = trailing;
  return node;
}

/**
 * Full conversation tree: the run whose id is `rootRunId` first, then its
 * descendants nested by `parent_run_id`. Runs whose parent is outside the
 * fetched window are attached to the root so nothing is hidden.
 */
export function buildConversationTree(events, rootRunId) {
  const list = events || [];
  const runs = splitByRun(list);
  // Event -> position in the caller's array, for ContextMeter's estimator.
  const originalIndex = new Map();
  list.forEach((ev, i) => originalIndex.set(ev, i));

  const nodes = new Map();
  for (const [runId, run] of runs) {
    nodes.set(runId, {
      runId,
      agentId: run.agentId,
      parentRunId: run.parentRunId,
      eventCount: run.events.length,
      children: [],
      trailingChildren: [],
      ...buildRunConversation(run.events, originalIndex),
    });
  }

  const roots = [];
  for (const node of nodes.values()) {
    const parent =
      node.parentRunId && node.parentRunId !== node.runId
        ? nodes.get(node.parentRunId)
        : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }

  // Children are rendered at the call that spawned them, not in a trailing
  // heap — do this after the tree is linked so every node has its children.
  for (const node of nodes.values()) attachChildrenToCalls(node);

  // Put the run being viewed first; orphans (parent outside the window) follow.
  roots.sort((a, b) => {
    if (a.runId === rootRunId) return -1;
    if (b.runId === rootRunId) return 1;
    return (a.runId || "").localeCompare(b.runId || "");
  });
  return roots;
}

/** Counts for the header, so an operator sees the shape before expanding. */
export function conversationStats(nodes) {
  let turns = 0;
  let tools = 0;
  let errors = 0;
  let warnings = 0;
  let costUsd = 0;
  let runs = 0;
  const walk = (list) => {
    for (const n of list) {
      runs += 1;
      turns += n.turns.length;
      const own = runTotals(n);
      errors += own.errors;
      warnings += own.warnings;
      for (const t of n.turns) costUsd += t.costUsd || 0;
      tools += own.tools;
      walk(n.children);
    }
  };
  walk(nodes || []);
  return { runs, turns, tools, errors, warnings, costUsd };
}

/** Per-run counters, excluding children. Shared by the header and the stats. */
export function runTotals(node) {
  let tools = 0;
  let errors = 0;
  let warnings = 0;
  let costUsd = 0;
  for (const t of node.turns || []) {
    costUsd += t.costUsd || 0;
    for (const it of t.items) {
      if (it.kind === "tool") tools += 1;
      if (it.isError) errors += 1;
      else if (it.isWarning) warnings += 1;
    }
  }
  return { tools, errors, warnings, costUsd };
}
